// Carrito de la venta de accesorios: estado puro (SKU → cantidad) y sus totales.
import type { CatalogItem } from '@/domains/sales/wizard/types';

/** SKU → unidades, en el orden en que se agregaron. */
export type CartQuantities = Readonly<Record<string, number>>;

export type CartAction =
    | { type: 'add'; sku: string }
    | { type: 'change'; sku: string; delta: number }
    | { type: 'remove'; sku: string }
    | { type: 'clear' };

export function cartReducer(cart: CartQuantities, action: CartAction): CartQuantities {
    switch (action.type) {
        case 'add':
            return { ...cart, [action.sku]: (cart[action.sku] ?? 0) + 1 };
        case 'change': {
            const next = (cart[action.sku] ?? 0) + action.delta;
            if (next > 0) return { ...cart, [action.sku]: next };
            return withoutSku(cart, action.sku);
        }
        case 'remove':
            return withoutSku(cart, action.sku);
        case 'clear':
            return {};
    }
}

function withoutSku(cart: CartQuantities, sku: string): CartQuantities {
    return Object.fromEntries(Object.entries(cart).filter(([key]) => key !== sku));
}

export interface CartLine {
    item: CatalogItem;
    qty: number;
    lineTotalCents: number;
}

export interface CartSummary {
    lines: CartLine[];
    itemsCount: number;
    unitsCount: number;
    subtotalCents: number;
}

/** Cruza el carrito con el catálogo; un SKU que el catálogo ya no trae se cae del total. */
export function summarizeCart(cart: CartQuantities, catalog: readonly CatalogItem[]): CartSummary {
    const bySku = new Map(catalog.map((item) => [item.sku, item]));
    const lines = Object.entries(cart).flatMap(([sku, qty]) => {
        const item = bySku.get(sku);
        return item ? [{ item, qty, lineTotalCents: item.cashPriceCents * qty }] : [];
    });
    return {
        lines,
        itemsCount: lines.length,
        unitsCount: lines.reduce((sum, line) => sum + line.qty, 0),
        subtotalCents: lines.reduce((sum, line) => sum + line.lineTotalCents, 0),
    };
}

/** «0 items · 0 unidades», con el plural del mock. */
export function cartCountLabel(itemsCount: number, unitsCount: number): string {
    const items = `${String(itemsCount)} item${itemsCount === 1 ? '' : 's'}`;
    const units = `${String(unitsCount)} unidad${unitsCount === 1 ? '' : 'es'}`;
    return `${items} · ${units}`;
}
